import { getIntersectionRoot, getScrollContainer } from '@theme/scroll-container';

const SCIENCE_JUMP_SELECTOR = '[data-testid="sybil-science-hero-jump"]';
const SCIENCE_REPLAY_SELECTOR = '[data-sybil-replay="true"]';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Replays a Science illustration when it receives a deliberate pointer or
 * keyboard interaction. The CSS owns the animation and reduced-motion state.
 */
function initializeScienceReplay() {
  const targets = [...document.querySelectorAll(SCIENCE_REPLAY_SELECTOR)];
  if (!targets.length) return;

  const motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  const clearReplay = () => {
    targets.forEach((target) => target.classList.remove('is-replaying'));
  };
  const startReplay = (event) => {
    if (motionQuery.matches) return;

    const target = event.currentTarget;
    target.classList.remove('is-replaying');
    requestAnimationFrame(() => {
      if (!target.isConnected || motionQuery.matches) return;
      target.classList.add('is-replaying');
    });
  };
  const endReplay = (event) => {
    const target = event.currentTarget;
    if (
      event.type === 'focusout' &&
      event.relatedTarget instanceof Node &&
      target.contains(event.relatedTarget)
    ) {
      return;
    }
    target.classList.remove('is-replaying');
  };

  targets.forEach((target) => {
    if (target.dataset.sybilReplayReady === 'true') return;

    target.dataset.sybilReplayReady = 'true';
    target.addEventListener('pointerenter', startReplay);
    target.addEventListener('pointerleave', endReplay);
    target.addEventListener('focusin', startReplay);
    target.addEventListener('focusout', endReplay);
  });

  motionQuery.addEventListener('change', (event) => {
    if (event.matches) clearReplay();
  });
}

/**
 * Measures chrome that can cover an anchored section.
 *
 * @returns {number}
 */
function getStickyChromeOffset() {
  const header = document.querySelector('#header-component[sticky]');
  const announcement = document.querySelector('#header-group .announcement-bar');
  let offset = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;

  if (announcement instanceof HTMLElement) {
    let element = announcement;
    let isSticky = false;

    while (element && element.id !== 'header-group') {
      const position = window.getComputedStyle(element).position;
      if (position === 'sticky' || position === 'fixed') {
        isSticky = true;
        break;
      }
      element = element.parentElement;
    }

    if (isSticky) offset += announcement.getBoundingClientRect().height;
  }

  return offset;
}

/**
 * Returns an anchor position relative to Horizon's active scroll root.
 *
 * @param {HTMLElement} target
 * @param {Element} scrollContainer
 * @returns {number}
 */
function getAnchorScrollTop(target, scrollContainer) {
  const targetRect = target.getBoundingClientRect();
  const offset = getStickyChromeOffset();

  if (scrollContainer === document.scrollingElement || scrollContainer === document.documentElement) {
    return Math.max(0, targetRect.top + scrollContainer.scrollTop - offset);
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  return Math.max(0, targetRect.top - containerRect.top + scrollContainer.scrollTop - offset);
}

/**
 * Enhances native science hash links for Horizon's desktop scroll container.
 *
 * @param {MouseEvent} event
 */
function handleScienceJump(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const source = event.target;
  if (!(source instanceof Element)) return;

  const link = source.closest(SCIENCE_JUMP_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;

  const destination = new URL(link.href, window.location.href);
  if (
    destination.origin !== window.location.origin ||
    destination.pathname !== window.location.pathname ||
    destination.search !== window.location.search ||
    !destination.hash
  ) {
    return;
  }

  let targetId;
  try {
    targetId = decodeURIComponent(destination.hash.slice(1));
  } catch (_) {
    return;
  }

  const target = document.getElementById(targetId);
  if (!(target instanceof HTMLElement)) return;

  event.preventDefault();

  const scrollContainer = getScrollContainer();
  const top = getAnchorScrollTop(target, scrollContainer);
  const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
  const nextState = { ...currentState, scrollTop: top };

  if (window.location.hash === destination.hash) {
    history.replaceState(nextState, '', destination);
  } else {
    history.pushState(nextState, '', destination);
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  scrollContainer.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
}

document.addEventListener('click', handleScienceJump);
initializeScienceReplay();

class SybilCountUp extends HTMLElement {
  #observer;
  #motionQuery;
  #motionController;
  #started = false;

  connectedCallback() {
    this.#observer?.disconnect();
    this.#motionController?.abort();
    this.#motionController = new AbortController();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#handleMotionChange, {
      signal: this.#motionController.signal,
    });

    if (this.#motionQuery.matches) {
      this.#showFinal();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      root: getIntersectionRoot(),
      threshold: 0.4,
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#motionController?.abort();
  }

  #handleIntersection = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting) || this.#started) return;
    this.#started = true;
    this.#animate();
    this.#observer?.unobserve(this);
    this.#observer?.disconnect();
  };

  #handleMotionChange = (event) => {
    if (event.matches) this.#showFinal();
  };

  #showFinal = () => {
    const target = Number(this.dataset.value || 0);
    const decimals = Number(this.dataset.decimals || 0);
    const prefix = this.dataset.prefix || '';
    const suffix = this.dataset.suffix || '';
    this.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
    this.classList.add('is-complete');
  };

  #animate = () => {
    const target = Number(this.dataset.value || 0);
    const decimals = Number(this.dataset.decimals || 0);
    const prefix = this.dataset.prefix || '';
    const suffix = this.dataset.suffix || '';
    const delay = Number(this.dataset.delay || 0);
    const duration = 1400;
    const start = performance.now() + delay;

    const tick = (now) => {
      const progress = Math.max(0, Math.min(1, (now - start) / duration));
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = `${prefix}${(target * eased).toFixed(decimals)}${suffix}`;
      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }
      this.classList.add('is-complete');
    };

    requestAnimationFrame(tick);
  };
}

class SybilInView extends HTMLElement {
  #observer;
  #motionQuery;
  #motionController;

  connectedCallback() {
    this.#observer?.disconnect();
    this.#motionController?.abort();
    this.#motionController = new AbortController();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#handleMotionChange, {
      signal: this.#motionController.signal,
    });

    if (this.#motionQuery.matches) {
      this.#activate();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      root: getIntersectionRoot(),
      threshold: Number(this.dataset.threshold || 0.3),
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#motionController?.abort();
  }

  #handleIntersection = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#activate();
  };

  #handleMotionChange = (event) => {
    if (event.matches) this.#activate();
  };

  #activate = () => {
    this.classList.add('is-active');
    this.#observer?.unobserve(this);
    this.#observer?.disconnect();
  };
}

if (!customElements.get('sybil-count-up')) {
  customElements.define('sybil-count-up', SybilCountUp);
}

if (!customElements.get('sybil-in-view')) {
  customElements.define('sybil-in-view', SybilInView);
}
